import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import clsx from 'clsx';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_ABBR = MONTH_NAMES.map((m) => m.slice(0, 3));

const POPOVER_WIDTH = 288; // w-72

function pad(n) {
  return String(n).padStart(2, '0');
}

// Accepts 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm', returns a local Date at
// midnight for the date part (or null).
function toDateOnly(value) {
  if (!value) return null;
  const [datePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toTimePart(value) {
  if (!value || !value.includes('T')) return '23:59';
  return value.split('T')[1]?.slice(0, 5) || '23:59';
}

function formatDateForValue(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// The typed/displayed text representation — dd/mm/yyyy[ hh:mm]. Kept
// distinct from the stored value format (which stays yyyy-mm-dd[Thh:mm] so
// callers/backends see no change).
function toTypedText(value, withTime) {
  const date = toDateOnly(value);
  if (!date) return '';
  const dateStr = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  return withTime ? `${dateStr} ${toTimePart(value)}` : dateStr;
}

function parseTypedText(text, withTime) {
  const trimmed = text.trim();
  if (!trimmed) return { valid: true, value: '' };

  const re = withTime
    ? /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
    : /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/;
  const m = trimmed.match(re);
  if (!m) return { valid: false };

  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12) return { valid: false };
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return { valid: false };
  }

  const dateStr = formatDateForValue(date);
  if (!withTime) return { valid: true, value: dateStr, date };

  let timeStr = '23:59';
  if (m[4] != null) {
    const hh = Number(m[4]);
    const mm = Number(m[5]);
    if (hh > 23 || mm > 59) return { valid: false };
    timeStr = `${pad(hh)}:${pad(mm)}`;
  }
  return { valid: true, value: `${dateStr}T${timeStr}`, date };
}

function isSameDay(a, b) {
  return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Shared date/datetime field — a typeable input plus a calendar popover.
 * Month/year navigation is a drill-down grid (click "August 2026" to pick
 * a month from a 12-tile grid, with year prev/next above it) rather than a
 * native <select> — selects render their option list with OS/browser chrome
 * that ignores the app's theme and looks inconsistent across browsers.
 *
 * The popover renders through a portal into document.body, positioned with
 * fixed coordinates measured from the trigger — it has to escape the DOM
 * here, not just get a high z-index, because callers like the Offers/
 * PromoCodes forms live inside Modal's `overflow-y-auto` content area,
 * which clips any plain `absolute` child rendered outside its bounds.
 *
 * Stored value format matches what native date inputs already produced —
 * this drops in without changing what callers store/submit:
 *   - withTime=false: 'YYYY-MM-DD'
 *   - withTime=true:  'YYYY-MM-DDTHH:mm'
 * Typed/displayed text is 'dd/mm/yyyy' (or 'dd/mm/yyyy hh:mm' with time).
 */
export default function DatePicker({
  label,
  required,
  value,
  onChange,
  withTime = false,
  min,
  max,
  placeholder,
  clearable = true,
  className,
  size = 'md', // 'md' matches Input.jsx (forms); 'sm' matches compact filter-bar controls
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('days'); // 'days' | 'months'
  const [coords, setCoords] = useState(null);
  const [text, setText] = useState(() => toTypedText(value, withTime));
  const selected = toDateOnly(value);
  const [viewMonth, setViewMonth] = useState(selected ?? new Date());
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  // Re-sync the editable text whenever the value changes from outside a
  // keystroke (calendar pick, time input, parent reset, or a successful
  // typed commit) — never while the user is mid-typing, since typing alone
  // doesn't touch `value` until blur/Enter.
  useEffect(() => {
    setText(toTypedText(value, withTime));
  }, [value, withTime]);

  useEffect(() => {
    if (open) {
      setViewMonth(selected ?? new Date());
      setView('days');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Position the portaled popover against the trigger, flipping above it
  // when there isn't enough room below (e.g. a field near the bottom of a
  // modal) — measured post-commit so it uses the popover's real height.
  useLayoutEffect(() => {
    if (!open) return;

    function updatePosition() {
      const trigger = wrapperRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.offsetHeight ?? 320;
      const gap = 6;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < popoverHeight + gap && rect.top > popoverHeight + gap;

      setCoords({
        top: openUpward ? rect.top - popoverHeight - gap : rect.bottom + gap,
        left: Math.min(Math.max(rect.left, 8), window.innerWidth - POPOVER_WIDTH - 8),
      });
    }

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, view, viewMonth, withTime]);

  useEffect(() => {
    function handleClickOutside(e) {
      const insideTrigger = wrapperRef.current?.contains(e.target);
      const insidePopover = popoverRef.current?.contains(e.target);
      if (!insideTrigger && !insidePopover) setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const minDate = min ? toDateOnly(min) : null;
  const maxDate = max ? toDateOnly(max) : null;

  function isDisabled(date) {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  }

  function isMonthDisabled(y, m) {
    const firstDay = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);
    if (maxDate && firstDay > maxDate) return true;
    if (minDate && lastDay < minDate) return true;
    return false;
  }

  function commitDate(date) {
    const dateStr = formatDateForValue(date);
    onChange(withTime ? `${dateStr}T${toTimePart(value)}` : dateStr);
    if (!withTime) setOpen(false);
  }

  function commitTime(time) {
    const dateStr = formatDateForValue(selected ?? new Date());
    onChange(`${dateStr}T${time}`);
  }

  function commitTypedText() {
    const result = parseTypedText(text, withTime);
    if (!result.valid) {
      setText(toTypedText(value, withTime));
      return;
    }
    if (result.value === '') {
      if (value) onChange('');
      return;
    }
    if (isDisabled(result.date)) {
      setText(toTypedText(value, withTime));
      return;
    }
    onChange(result.value);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitTypedText();
    } else if (e.key === 'Escape') {
      setText(toTypedText(value, withTime));
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const startWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const currentYear = today.getFullYear();
  const minYear = minDate ? minDate.getFullYear() : currentYear - 10;
  const maxYear = maxDate ? maxDate.getFullYear() : currentYear + 10;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <div ref={wrapperRef} className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Calendar size={16} className="text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={commitTypedText}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? (withTime ? 'dd/mm/yyyy hh:mm' : 'dd/mm/yyyy')}
          className={clsx(
            'block w-full rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 shadow-sm transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500',
            'border-gray-300 dark:border-gray-600',
            'pl-10',
            clearable && value ? 'pr-10' : 'pr-3',
            size === 'sm' ? 'py-2 text-sm' : 'py-2.5 text-sm',
            className
          )}
        />
        {clearable && value && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(''); inputRef.current?.focus(); }}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Clear date"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            top: coords?.top ?? -9999,
            left: coords?.left ?? -9999,
            width: POPOVER_WIDTH,
            visibility: coords ? 'visible' : 'hidden',
          }}
          className="z-[1000] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-2xl"
        >
          {view === 'days' ? (
            <>
              <div className="flex items-center justify-between mb-2 gap-1">
                <button
                  type="button"
                  onClick={() => setViewMonth(new Date(year, month - 1, 1))}
                  className="shrink-0 rounded-lg p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setView('months')}
                  className="rounded-lg px-2 py-1 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {MONTH_NAMES[month]} {year}
                </button>
                <button
                  type="button"
                  onClick={() => setViewMonth(new Date(year, month + 1, 1))}
                  className="shrink-0 rounded-lg p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-1">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="text-center text-[11px] font-medium text-gray-400 dark:text-gray-500 py-1">{w}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((date, i) => {
                  if (!date) return <div key={`empty-${i}`} />;
                  const disabled = isDisabled(date);
                  const isSelected = isSameDay(date, selected);
                  const isToday = isSameDay(date, today);
                  return (
                    <button
                      key={date.toISOString()}
                      type="button"
                      disabled={disabled}
                      onClick={() => commitDate(date)}
                      className={clsx(
                        'h-8 w-8 rounded-lg text-sm transition-colors',
                        disabled && 'text-gray-300 dark:text-gray-600 cursor-not-allowed',
                        !disabled && !isSelected && 'text-gray-700 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-amber-900/20',
                        isSelected && 'bg-amber-500 text-gray-900 font-semibold',
                        !isSelected && isToday && 'ring-1 ring-inset ring-amber-400'
                      )}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>

              {withTime && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 shrink-0">Time</span>
                  <input
                    type="time"
                    value={toTimePart(value)}
                    onChange={(e) => commitTime(e.target.value)}
                    className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500 [color-scheme:light] dark:[color-scheme:dark]"
                  />
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="shrink-0 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-gray-900 hover:bg-amber-600"
                  >
                    Done
                  </button>
                </div>
              )}

              <div className="mt-2 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => commitDate(today)}
                  className="text-xs font-medium text-amber-600 dark:text-amber-400 hover:underline"
                >
                  Today
                </button>
                {clearable && value && (
                  <button
                    type="button"
                    onClick={() => { onChange(''); setOpen(false); }}
                    className="text-xs font-medium text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    Clear
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  disabled={year <= minYear}
                  onClick={() => setViewMonth(new Date(year - 1, month, 1))}
                  className="shrink-0 rounded-lg p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{year}</span>
                <button
                  type="button"
                  disabled={year >= maxYear}
                  onClick={() => setViewMonth(new Date(year + 1, month, 1))}
                  className="shrink-0 rounded-lg p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {MONTH_ABBR.map((abbr, i) => {
                  const disabled = isMonthDisabled(year, i);
                  const isCurrent = i === month;
                  return (
                    <button
                      key={abbr}
                      type="button"
                      disabled={disabled}
                      onClick={() => { setViewMonth(new Date(year, i, 1)); setView('days'); }}
                      className={clsx(
                        'rounded-lg py-2 text-sm font-medium transition-colors',
                        disabled && 'text-gray-300 dark:text-gray-600 cursor-not-allowed',
                        !disabled && !isCurrent && 'text-gray-700 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-amber-900/20',
                        isCurrent && 'bg-amber-500 text-gray-900 font-semibold'
                      )}
                    >
                      {abbr}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
